import { WIDGET_DEFAULTS, WIDGET_FONT_IDS, contrastRatio, relativeLuminance, toWidgetFontId } from "@sagot/constants";
import { z } from "zod";
import type { WidgetDraftIn } from "../shared/types";
import { homeFormSchema, toHomeDraft, toHomeForm } from "./home-model";

/** Widget Studio limits — mirror backend `app/core/constants/channels.py` + widget schemas. */
export const WIDGET_LIMITS = {
  maxStarters: 4,
  radiusMin: 0,
  radiusMax: 28,
  radiusStep: 2,
  charLimitMin: 100,
  charLimitMax: 2000,
  delayMaxS: 120,
  maxUrlPatterns: 20,
  rememberMaxDays: 30,
  maxDomains: 50,
  maxBlockedIps: 200,
  avatarMaxBytes: 2 * 1024 * 1024,
  /** Launcher icon side range in px (backend `WIDGET_LAUNCHER_ICON_MIN_SIDE` / `_MAX_SIDE`). */
  launcherIconMinPx: 64,
  launcherIconMaxPx: 4096,
  minBrandContrast: 3,
  aaContrast: 4.5,
} as const;

export const AVATAR_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export const WIDGET_TABS = ["appearance", "content", "home", "behavior", "install", "security"] as const;
export type WidgetTab = (typeof WIDGET_TABS)[number];

export function isWidgetTab(value: string | undefined): value is WidgetTab {
  return WIDGET_TABS.some((tab) => tab === value);
}

/** Brand presets (tenant data, not UI chrome): applied through `--wg-primary` only. */
export const BRAND_PRESETS = [
  { key: "brand", hex: WIDGET_DEFAULTS.primary },
  { key: "maroon", hex: "#7a1f2b" },
  { key: "forest", hex: "#1f6b4a" },
  { key: "navy", hex: "#1d3a8a" },
  { key: "amber", hex: "#f5a400" },
  { key: "ink", hex: "#080c1c" },
] as const;

const ON_NAVY = WIDGET_DEFAULTS.onPrimary;
const ON_WHITE = "#ffffff";
const HEX_PATTERN = /^#[\da-f]{6}$/i;
const RATIO_DECIMALS = 100;

export interface BrandContrast {
  valid: boolean;
  onPrimary: string;
  tone: "dark" | "light";
  ratio: number;
  meetsAa: boolean;
  /** Below 3:1 with both navy and white: the API rejects it. */
  tooLow: boolean;
}

/** Same rule as backend `on_color`: navy or white, whichever contrasts more (ties → navy). */
export function brandContrast(hex: string): BrandContrast {
  const luminance = HEX_PATTERN.test(hex) ? relativeLuminance(hex) : null;
  if (luminance === null) return { valid: false, onPrimary: ON_NAVY, tone: "dark", ratio: 0, meetsAa: false, tooLow: false };
  const dark = contrastRatio(luminance, relativeLuminance(ON_NAVY) ?? 0);
  const light = contrastRatio(luminance, relativeLuminance(ON_WHITE) ?? 1);
  const useDark = dark >= light;
  const ratio = Math.round((useDark ? dark : light) * RATIO_DECIMALS) / RATIO_DECIMALS;
  return {
    valid: true,
    onPrimary: useDark ? ON_NAVY : ON_WHITE,
    tone: useDark ? "dark" : "light",
    ratio,
    meetsAa: ratio >= WIDGET_LIMITS.aaContrast,
    tooLow: ratio < WIDGET_LIMITS.minBrandContrast,
  };
}

const httpsUrl = z.union([z.literal(""), z.string().trim().max(2048).regex(/^https:\/\/\S+$/, "privacyUrlInvalid")]);

export const widgetFormSchema = z.object({
  appearance: z.object({
    primary_color: z.string().regex(HEX_PATTERN, "invalidHex"),
    theme: z.enum(["light", "dark", "auto"]),
    radius_px: z.number().int().min(WIDGET_LIMITS.radiusMin).max(WIDGET_LIMITS.radiusMax),
    font: z.enum(WIDGET_FONT_IDS),
    launcher_style: z.enum(["icon", "label"]),
    launcher_label: z.string().trim().max(30),
    position: z.enum(["left", "right"]),
    avatar_url: z.string().nullable(),
    use_agent_avatar: z.boolean(),
    launcher_icon_url: z.string().nullable(),
    show_branding: z.boolean(),
  }),
  content: z.object({
    title: z.string().trim().max(60),
    subtitle: z.string().trim().max(80),
    welcome_message: z.string().trim().max(500),
    starter_questions: z.array(z.string().trim().max(120)).length(WIDGET_LIMITS.maxStarters),
    placeholder: z.string().trim().max(80),
    privacy_notice: z.string().trim().max(500),
    privacy_url: httpsUrl,
    char_limit: z.number().int().min(WIDGET_LIMITS.charLimitMin).max(WIDGET_LIMITS.charLimitMax),
    attachments: z.enum(["off", "images", "images_pdf"]),
  }),
  home: homeFormSchema,
  behavior: z.object({
    auto_open: z.object({ enabled: z.boolean(), delay_s: z.number().int().min(0).max(WIDGET_LIMITS.delayMaxS) }),
    proactive: z.object({
      enabled: z.boolean(),
      message: z.string().trim().max(120),
      delay_s: z.number().int().min(0).max(WIDGET_LIMITS.delayMaxS),
      url_patterns: z.string().max(4000),
    }),
    show_on_mobile: z.boolean(),
    lead_capture: z.enum(["off", "optional", "required"]),
    out_of_hours: z.enum(["callback", "hide_handoff", "offline"]),
    sound: z.boolean(),
    remember_days: z.number().int().min(0).max(WIDGET_LIMITS.rememberMaxDays),
  }),
});
export type WidgetForm = z.infer<typeof widgetFormSchema>;

type Section = Record<string, unknown>;
const str = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);
const num = (value: unknown, fallback: number): number => (typeof value === "number" && Number.isFinite(value) ? value : fallback);
const bool = (value: unknown, fallback: boolean): boolean => (typeof value === "boolean" ? value : fallback);
function pick<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.find((option) => option === value) ?? fallback;
}
const section = (value: unknown): Section => (value && typeof value === "object" ? (value as Section) : {});

function starters(value: unknown): string[] {
  const list = Array.isArray(value) ? value.filter((q): q is string => typeof q === "string") : [];
  return Array.from({ length: WIDGET_LIMITS.maxStarters }, (_, i) => list[i] ?? "");
}

function appearanceForm(raw: Section): WidgetForm["appearance"] {
  return {
    primary_color: str(raw.primary_color, WIDGET_DEFAULTS.primary).toLowerCase(),
    theme: pick(raw.theme, ["light", "dark", "auto"], "light"),
    radius_px: num(raw.radius_px, WIDGET_DEFAULTS.radiusPx),
    font: toWidgetFontId(raw.font),
    launcher_style: pick(raw.launcher_style, ["icon", "label"], "icon"),
    launcher_label: str(raw.launcher_label),
    position: pick(raw.position, ["left", "right"], "right"),
    avatar_url: typeof raw.avatar_url === "string" ? raw.avatar_url : null,
    use_agent_avatar: bool(raw.use_agent_avatar, true),
    launcher_icon_url: typeof raw.launcher_icon_url === "string" ? raw.launcher_icon_url : null,
    show_branding: bool(raw.show_branding, true),
  };
}

function contentForm(raw: Section): WidgetForm["content"] {
  return {
    title: str(raw.title),
    subtitle: str(raw.subtitle),
    welcome_message: str(raw.welcome_message),
    starter_questions: starters(raw.starter_questions),
    placeholder: str(raw.placeholder),
    privacy_notice: str(raw.privacy_notice),
    privacy_url: str(raw.privacy_url),
    char_limit: num(raw.char_limit, 1000),
    attachments: pick(raw.attachments, ["off", "images", "images_pdf"], "off"),
  };
}

function behaviorForm(raw: Section): WidgetForm["behavior"] {
  const autoOpen = section(raw.auto_open);
  const proactive = section(raw.proactive);
  const patterns = Array.isArray(proactive.url_patterns) ? proactive.url_patterns.filter((p): p is string => typeof p === "string") : [];
  return {
    auto_open: { enabled: bool(autoOpen.enabled, false), delay_s: num(autoOpen.delay_s, 0) },
    proactive: { enabled: bool(proactive.enabled, false), message: str(proactive.message), delay_s: num(proactive.delay_s, 0), url_patterns: patterns.join(", ") },
    show_on_mobile: bool(raw.show_on_mobile, true),
    lead_capture: pick(raw.lead_capture, ["off", "optional", "required"], "off"),
    out_of_hours: pick(raw.out_of_hours, ["callback", "hide_handoff", "offline"], "callback"),
    sound: bool(raw.sound, false),
    remember_days: num(raw.remember_days, 7),
  };
}

/** Server draft (`WidgetOut.draft`, loosely typed) → complete form values. */
export function toWidgetForm(draft: Record<string, unknown>): WidgetForm {
  return {
    appearance: appearanceForm(section(draft.appearance)),
    content: contentForm(section(draft.content)),
    home: toHomeForm(section(draft.content).home),
    behavior: behaviorForm(section(draft.behavior)),
  };
}

export function splitPatterns(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, WIDGET_LIMITS.maxUrlPatterns);
}

/**
 * "Powered by" can only be hidden on plans with the remove-branding feature. A draft saved while the
 * plan allowed it (or seeded) would otherwise make every later save answer 402 with the switch locked
 * "on" and no way to fix it, so a locked plan always saves the badge as shown.
 */
export function enforceBrandingPlan(form: WidgetForm, brandingLocked: boolean): WidgetForm {
  if (!brandingLocked || form.appearance.show_branding) return form;
  return { ...form, appearance: { ...form.appearance, show_branding: true } };
}

/** Form → `PUT /v1/agents/{id}/widget` body. Lead fields are kept as stored (edited elsewhere). */
export function toDraftIn(form: WidgetForm, storedLeadFields: WidgetDraftIn["behavior"]["lead_fields"] = []): WidgetDraftIn {
  const { proactive, ...behavior } = form.behavior;
  return {
    appearance: { ...form.appearance, primary_color: form.appearance.primary_color.toLowerCase() },
    content: {
      ...form.content,
      starter_questions: form.content.starter_questions.map((q) => q.trim()).filter(Boolean),
      privacy_url: form.content.privacy_url.trim() || null,
      home: toHomeDraft(form.home),
    },
    behavior: { ...behavior, proactive: { ...proactive, url_patterns: splitPatterns(proactive.url_patterns) }, lead_fields: storedLeadFields },
  };
}

/** Stored lead fields from the raw draft (kept untouched on save). */
export function storedLeadFields(draft: Record<string, unknown>): WidgetDraftIn["behavior"]["lead_fields"] {
  const fields = section(draft.behavior).lead_fields;
  if (!Array.isArray(fields)) return [];
  return fields.flatMap((f) => {
    const field = section(f);
    if (typeof field.key !== "string" || typeof field.label !== "string") return [];
    return [{ key: field.key, label: field.label, required: field.required === true }];
  });
}
